<template>
  <div class="table-list-container" :class="{ 'no-container': tables.length === 0 }">
    <div v-if="loading" class="loading-container">
      <AppPageLoader>{{ $t('general.loading') }}</AppPageLoader>
    </div>

    <div v-else-if="error" class="error-container">
      <font-awesome-icon icon="fa-exclamation-triangle" class="error-icon" />
      <h2>{{ $t('forms.errorLoading') }}</h2>
      <p>{{ error }}</p>
    </div>

    <div v-else-if="tables.length === 0">
      <EmptyState icon="fa-table" :title="$t('tables.noTables')" :message="$t('tables.addTablePrompt')">
      </EmptyState>
    </div>

    <div v-else class="tables-content">
      <div class="tables-grid">
        <div v-for="table in tables" :key="table.uuid" class="table-card">
          <div class="table-card-header">
            <h3>{{ table.name }}</h3>
            <span class="status-badge" :class="{ active: table.is_active }">
              {{ table.is_active ? $t('establishments.active') : $t('establishments.inactive') }}
            </span>
          </div>
          <div class="table-card-body">
            <div class="info-list">
              <div class="info-item">
                <font-awesome-icon icon="fa-hashtag" class="info-icon" />
                <span class="info-text">{{ $t('tables.tableNumber') }}: {{ table.number }}</span>
              </div>
              <div class="info-item">
                <font-awesome-icon icon="fa-users" class="info-icon" />
                <span class="info-text">{{ $t('tables.capacity') }}: {{ table.capacity || $t('tables.notSpecified')
                }}</span>
              </div>
            </div>
          </div>
          <div v-if="!readOnly" class="table-card-footer">
            <button type="button" @click="editTable(table)" class="action-btn edit">
              <font-awesome-icon icon="fa-pen" />
            </button>
            <button type="button" @click="generateQR(table)" class="action-btn qr">
              <font-awesome-icon icon="fa-qrcode" />
            </button>
            <button type="button" @click="confirmDelete(table)" class="action-btn delete">
              <font-awesome-icon icon="fa-trash" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <TableFormModal v-if="showTableModal" :table="selectedTable" :location-id="locationId"
      @close="showTableModal = false" @saved="handleTableSaved" />

    <TableBatchFormModal v-if="showBatchModal" :location-id="locationId" @close="showBatchModal = false"
      @saved="handleBatchSaved" />

    <QRCodeModal v-if="showQRModal" :table="selectedTable" :location-id="locationId" :single-mode="singleQRMode"
      @close="showQRModal = false" />

    <div v-if="showDeleteConfirm" class="modal-backdrop">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h3>{{ $t('tables.confirmDelete') }}</h3>
            <button @click="showDeleteConfirm = false" class="close-btn">
              <font-awesome-icon icon="fa-times" />
            </button>
          </div>
          <div class="modal-body">
            <p>{{ $t('tables.deleteConfirmMessage') }} <strong>{{ selectedTable?.name }}</strong>?</p>
            <p class="warning">
              <font-awesome-icon icon="fa-exclamation-triangle" />
              {{ $t('tables.deleteConfirmWarning') }}
            </p>
          </div>
          <div class="modal-footer">
            <BaseButton variant="secondary" @click="showDeleteConfirm = false">
              {{ $t('forms.cancel') }}
            </BaseButton>
            <BaseButton variant="danger" :loading="isDeleting" icon="fa-trash" @click="deleteTable">
              {{ isDeleting ? $t('forms.deleting') : $t('forms.delete') }}
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { tableApi } from '@/services/api';
import { Table } from '@/types/tables';
import { useI18n } from 'vue-i18n';
import TableFormModal from './TableFormModal.vue';
import TableBatchFormModal from './TableBatchFormModal.vue';
import QRCodeModal from './QRCodeModal.vue';
import EmptyState from '../shared/EmptyState.vue';
import BaseButton from '@/components/shared/BaseButton.vue';

const props = defineProps({
  locationId: {
    type: String,
    required: true
  },
  readOnly: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['refresh']);
const { t } = useI18n();

const tables = ref<Table[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);
const selectedTable = ref<Table | null>(null);

const showTableModal = ref(false);
const showBatchModal = ref(false);
const showQRModal = ref(false);
const showDeleteConfirm = ref(false);
const singleQRMode = ref(true);
const isDeleting = ref(false);

const fetchTables = async () => {
  loading.value = true;
  error.value = null;

  try {
    tables.value = await tableApi.getTablesForLocation(props.locationId);
  } catch (err: any) {
    error.value = err.message || t('tables.errorFetchingTables');
  } finally {
    loading.value = false;
  }
};

const openCreateModal = () => {
  selectedTable.value = null;
  showTableModal.value = true;
};

const editTable = (table: Table) => {
  selectedTable.value = table;
  showTableModal.value = true;
};

const openBatchCreateModal = () => {
  showBatchModal.value = true;
};

const openGenerateQRModal = () => {
  selectedTable.value = null;
  singleQRMode.value = false;
  showQRModal.value = true;
};

const generateQR = (table: Table) => {
  selectedTable.value = table;
  singleQRMode.value = true;
  showQRModal.value = true;
};

const confirmDelete = (table: Table) => {
  selectedTable.value = table;
  showDeleteConfirm.value = true;
};

const deleteTable = async () => {
  if (!selectedTable.value) return;

  isDeleting.value = true;

  try {
    await tableApi.deleteTable(selectedTable.value.uuid);
    await fetchTables();
    showDeleteConfirm.value = false;
  } catch (err: any) {
    error.value = err.message || t('tables.errorDeletingTable');
  } finally {
    isDeleting.value = false;
  }
};

const handleTableSaved = async () => {
  showTableModal.value = false;
  await fetchTables();
};

const handleBatchSaved = async () => {
  showBatchModal.value = false;
  await fetchTables();
};

onMounted(() => {
  fetchTables();
});

defineExpose({
  refresh: fetchTables
});
</script>

<style scoped>
.table-list-container {
  width: 100%;
}

.table-list-container.no-container {
  background-color: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
}

.loading-container,
.error-container,
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

.error-icon {
  color: #e74c3c;
  font-size: 2rem;
  margin-bottom: 1rem;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
}

.empty-icon {
  font-size: 3rem;
  color: #bdc3c7;
  margin-bottom: 1rem;
}

.header-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.tables-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 320px));
  gap: 1.5rem;
}

.table-card {
  background-color: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  position: relative;
  border: 1px solid #f1f1f1;
  border-top: 4px solid #D8670C;
  min-width: 0;
}

.table-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.table-card-header {
  padding: 1rem 1.25rem 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

.table-card-header h3 {
  margin: 0;
  min-width: 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
  overflow-wrap: anywhere;
}

.status-badge {
  flex-shrink: 0;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #fee2e2;
  color: #ef4444;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.status-badge.active {
  background-color: #ecfdf5;
  color: #10b981;
}

.table-card-body {
  padding: 0 1.25rem 1rem;
  display: flex;
  flex-direction: column;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px dashed #e5e7eb;
}

.info-item:last-child {
  border-bottom: none;
}

.info-icon {
  margin-right: 0.75rem;
  width: 18px;
  height: 18px;
  color: #D8670C;
  opacity: 0.9;
}

.info-text {
  font-size: 0.95rem;
  color: #4b5563;
  font-weight: 500;
}

.table-card-footer {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background-color: #f9fafb;
  border-top: 1px solid #f3f4f6;
  border-radius: 0 0 12px 12px;
}

.action-btn {
  border: none;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.action-btn.edit {
  background-color: #f0f9ff;
  color: #2563eb;
}

.action-btn.edit:hover {
  background-color: #e0f2fe;
  color: #1e40af;
  transform: scale(1.1);
}

.action-btn.qr {
  background-color: #f0fdf4;
  color: #15803d;
}

.action-btn.qr:hover {
  background-color: #dcfce7;
  color: #166534;
  transform: scale(1.1);
}

.action-btn.delete {
  background-color: #fef2f2;
  color: #dc2626;
}

.action-btn.delete:hover {
  background-color: #fee2e2;
  color: #b91c1c;
  transform: scale(1.1);
}

@media (max-width: 640px) {
  .tables-grid {
    grid-template-columns: 1fr;
  }
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background-color: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.modal-content {
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #6b7280;
  cursor: pointer;
}

.modal-body {
  padding: 1.5rem;
}

.modal-body p {
  margin-top: 0;
  margin-bottom: 1rem;
}

.modal-body p:last-child {
  margin-bottom: 0;
}

.warning {
  color: #b91c1c;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
}
</style>